import { createContext, useCallback, useContext, useRef, useState } from "react";
import ConfirmDialog from "../components/common/ConfirmDialog";

const ConfirmContext = createContext(null);

const INITIAL_STATE = { open: false };

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(INITIAL_STATE);
  const resolver = useRef(null);

  /**
   * confirm({ title, message, confirmLabel, cancelLabel, variant })
   * → Promise<boolean>, resolves true on confirm, false on cancel/dismiss.
   */
  const confirm = useCallback((options = {}) => {
    // If a previous confirm is still pending, resolve it false before replacing it
    resolver.current?.(false);

    setState({
      open: true,
      title: options.title ?? "Are you sure?",
      message: options.message ?? "",
      confirmLabel: options.confirmLabel ?? "Confirm",
      cancelLabel: options.cancelLabel ?? "Cancel",
      variant: options.variant ?? "primary",
    });

    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const handleClose = (result) => {
    setState(INITIAL_STATE);
    resolver.current?.(result);
    resolver.current = null;
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      <ConfirmDialog
        {...state}
        onConfirm={() => handleClose(true)}
        onCancel={() => handleClose(false)}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);

  if (!ctx) {
    throw new Error("useConfirm must be used within a <ConfirmProvider>.");
  }

  return ctx;
};